/**
 * Step 3 — what is about to be created.
 *
 * Ends in a draft, always. The warning about not approving your own announcement is the BFF's
 * rule; showing it here only means the person learns it before writing, not after submitting.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { JSX } from 'react';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

const titleBlockStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const titleStyle = css`
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
`;

const subtitleStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
  gap: var(--space-m) var(--space-ml);
  padding: var(--space-m) 0;
  border-top: 1px solid var(--color-divider);
  border-bottom: 1px solid var(--color-divider);
`;

const cellStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const labelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const valueStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-wrap: pretty;
`;

const warningStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text);
  text-wrap: pretty;
`;

const errorStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-danger);
`;

export interface ReviewSummaryRow {
  label: string;
  value: string;
}

export interface StepReviewProps {
  rows: ReviewSummaryRow[];
  error?: string | null;
}

export const StepReview = ({ rows, error }: StepReviewProps): JSX.Element => (
  <div className={wrapStyle}>
    <div className={titleBlockStyle}>
      <h2 className={titleStyle}>Revisar e criar rascunho</h2>
      <p className={subtitleStyle}>
        O item é criado como rascunho. Depois de enviar para aprovação, ele fica bloqueado para
        edição até alguém aprovar ou rejeitar.
      </p>
    </div>

    <div className={gridStyle}>
      {rows.map(row => (
        <div key={row.label} className={cellStyle}>
          <span className={labelStyle}>{row.label}</span>
          <span className={valueStyle}>{row.value}</span>
        </div>
      ))}
    </div>

    <div className={warningStyle}>
      <GLIcon variant='Notifications.Warning' size={18} fill='var(--gl-yellow-dark)' />
      <span>
        Você não poderá aprovar este anúncio, porque é o criador. Outra pessoa com permissão de
        aprovação precisa revisar.
      </span>
    </div>

    {error ? <span className={errorStyle}>{error}</span> : null}
  </div>
);
